/**
 * =============================================================================
 * LISTING HELPERS - Utility Functions for Listings
 * =============================================================================
 * 
 * Shared utility functions for working with car part listings.
 * Used across multiple components and pages.
 * 
 * AVAILABLE FUNCTIONS:
 * - isListingNew() - Check if listing is less than 24 hours old
 * - formatListingPrice() - Format price with Swedish formatting
 * - getListingStatusLabel() - Get Swedish label for listing status
 * - getConditionLabel() - Get Swedish label for item condition
 * - truncateDescription() - Shorten long descriptions
 * 
 * @see src/pages/Search.tsx - Uses isListingNew for badges
 * @see src/components/ListingPreview.tsx - Uses formatting functions
 * =============================================================================
 */

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/** Possible listing status values */
export type ListingStatus = 'active' | 'sold' | 'removed' | 'pending';

/** Possible item condition values */
export type ItemCondition = 'nytt skick' | 'använt skick' | 'renoveringsobjekt';

// =============================================================================
// AGE & FRESHNESS UTILITIES
// =============================================================================

/**
 * Checks if a listing is considered "new" (less than 24 hours old).
 * Used to display "NY" badge on recent listings.
 * 
 * @param createdAt - ISO date string of when the listing was created
 * @returns true if listing is less than 24 hours old
 * 
 * @example
 * const listing = { created_at: "2024-01-15T10:00:00Z" };
 * if (isListingNew(listing.created_at)) {
 *   // Show "NY" badge
 * }
 */
export const isListingNew = (createdAt: string): boolean => {
  const now = new Date();
  const created = new Date(createdAt);
  const hoursDifference = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
  return hoursDifference < 24;
};

/**
 * Checks if a listing was updated recently (within last 7 days).
 * Can be used to show "Uppdaterad" indicators.
 * 
 * @param updatedAt - ISO date string of last update
 * @param createdAt - ISO date string of creation (to avoid showing on new listings)
 * @returns true if updated within 7 days and after creation
 */
export const wasRecentlyUpdated = (updatedAt: string, createdAt: string): boolean => {
  const now = new Date();
  const updated = new Date(updatedAt);
  const created = new Date(createdAt);
  
  // Don't show if it's just the creation date
  if (updated.getTime() - created.getTime() < 60000) return false;
  
  const daysDifference = (now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);
  return daysDifference < 7;
};

/**
 * Formats how long ago a listing was published.
 * < 60 min: "X min", < 24 h: "X t", < 7 days: "X d", otherwise the date.
 */
export const formatPublishedAt = (createdAt: string): string => {
  const now = new Date();
  const created = new Date(createdAt);
  const minutes = Math.floor((now.getTime() - created.getTime()) / 60000);

  if (minutes < 1) return "mindre än 1 min sedan";
  if (minutes < 60) return `${minutes} min sedan`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} t sedan`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} d sedan`;

  return created.toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// =============================================================================
// PRICE FORMATTING
// =============================================================================

/**
 * Formats a price number to Swedish currency format.
 * 
 * @param price - The price as a number
 * @param includeCurrency - Whether to include "kr" suffix (default: true)
 * @returns Formatted price string (e.g., "1 500 kr")
 * 
 * @example
 * formatListingPrice(1500) // "1 500 kr"
 * formatListingPrice(1500, false) // "1 500"
 * formatListingPrice(0) // "Gratis"
 */
export const formatListingPrice = (
  price: number, 
  includeCurrency: boolean = true
): string => {
  if (price === 0) return "Gratis";
  
  const formatted = price.toLocaleString('sv-SE');
  return includeCurrency ? `${formatted} kr` : formatted;
};

/**
 * Parses a price string back to a number.
 * Handles Swedish formatting with spaces.
 * 
 * @param priceString - The price string to parse
 * @returns The price as a number, or 0 if invalid
 * 
 * @example
 * parsePriceString("1 500 kr") // 1500
 * parsePriceString("Gratis") // 0
 */
export const parsePriceString = (priceString: string): number => {
  if (priceString.toLowerCase() === 'gratis') return 0;
  
  const cleaned = priceString.replace(/[^\d]/g, '');
  const parsed = parseInt(cleaned, 10);
  return isNaN(parsed) ? 0 : parsed;
};

// =============================================================================
// STATUS & CONDITION LABELS
// =============================================================================

/**
 * Gets the Swedish display label for a listing status.
 * 
 * @param status - The listing status
 * @returns Swedish label for the status
 * 
 * @example
 * getListingStatusLabel('active') // "Aktiv"
 * getListingStatusLabel('sold') // "Såld"
 */
export const getListingStatusLabel = (status: ListingStatus | string): string => {
  const labels: Record<string, string> = {
    'active': 'Aktiv',
    'sold': 'Såld',
    'removed': 'Borttagen',
    'pending': 'Väntar',
  };
  return labels[status] || status;
};

/**
 * Gets the Swedish display label for item condition.
 * 
 * @param condition - The item condition
 * @returns Swedish label for the condition
 * 
 * @example
 * getConditionLabel('nytt skick') // "Nytt skick"
 */
export const getConditionLabel = (condition: ItemCondition | string): string => {
  const labels: Record<string, string> = {
    'nytt skick': 'Nytt skick',
    'använt skick': 'Använt skick',
    'renoveringsobjekt': 'Renoveringsobjekt',
  };
  return labels[condition] || condition;
};

/**
 * Gets a color class for the listing status (for badges).
 * 
 * @param status - The listing status
 * @returns Tailwind color class
 */
export const getStatusColor = (status: ListingStatus | string): string => {
  const colors: Record<string, string> = {
    'active': 'bg-green-100 text-green-800',
    'sold': 'bg-blue-100 text-blue-800',
    'removed': 'bg-red-100 text-red-800',
    'pending': 'bg-yellow-100 text-yellow-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

// =============================================================================
// TEXT UTILITIES
// =============================================================================

/**
 * Truncates a description to a specified length with ellipsis.
 * 
 * @param description - The full description text
 * @param maxLength - Maximum characters to show (default: 100)
 * @returns Truncated string with "..." if needed
 * 
 * @example
 * truncateDescription("Very long text...", 50) // "Very long text..."
 */
export const truncateDescription = (
  description: string, 
  maxLength: number = 100
): string => {
  if (!description) return '';
  if (description.length <= maxLength) return description;
  
  // Cut at last space before maxLength to avoid cutting words
  const truncated = description.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 
    ? truncated.substring(0, lastSpace) + '...'
    : truncated + '...';
};

/**
 * Generates a URL-friendly slug from a listing title.
 * 
 * @param title - The listing title
 * @returns URL-safe slug
 * 
 * @example
 * createListingSlug("Volvo V70 Bakljus") // "volvo-v70-bakljus"
 */
export const createListingSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// =============================================================================
// SORTING & FILTERING HELPERS
// =============================================================================

/**
 * Sorting options for listings.
 */
export type SortOption = 'newest' | 'oldest' | 'price-low' | 'price-high';

/**
 * Gets the label for a sort option.
 * 
 * @param option - The sort option
 * @returns Swedish label
 */
export const getSortOptionLabel = (option: SortOption): string => {
  const labels: Record<SortOption, string> = {
    'newest': 'Nyast först',
    'oldest': 'Äldst först',
    'price-low': 'Lägst pris',
    'price-high': 'Högst pris',
  };
  return labels[option];
};
